import { TrendingUp, Check, Brain, Wand2 } from 'lucide-react';
import { motion } from 'framer-motion';
import DrivingForcesChecklist from './DrivingForcesChecklist';
import { useState, useCallback, useEffect } from 'react';
import { calculateDrivingForcesVolatility } from '../../../services/scoringService';
import { companiesApi } from '../../../services/api';

// AI Analysis Animation Component
const AIAnalysisIndicator = ({ isAnalyzing }: { isAnalyzing: boolean }) => {
  if (!isAnalyzing) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
      >
        <Brain className="h-4 w-4" />
      </motion.div>
      <span className="text-sm font-medium">Analyzing with AI</span>
      <motion.div
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <Wand2 className="h-3 w-3" />
      </motion.div>
    </motion.div>
  );
};

interface DrivingForcesSectionProps {
  companyId: string;
  externalData: {
    analysisQuestions: {
      demandTrend: string;
      competitionIntensity: string;
      profitabilityImpact: string;
    };
  };
  onUpdateAnalysisQuestion: (
    question: 'demandTrend' | 'competitionIntensity' | 'profitabilityImpact',
    value: string
  ) => void;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputChange: () => void;
  onProgressChange?: (progress: number) => void;
}

// Function to convert score to volatility level (based on absolute count)
const getVolatilityLevel = (score: number): string => {
  // -1 indicates no analysis has been done
  if (score < 0) return 'Not Analyzed';

  // Use absolute count: 1=Moderate, 2=High, 3+=Very High
  if (score >= 3) return 'Very High';
  if (score === 2) return 'High';
  if (score === 1) return 'Moderate';
  return 'Very Low'; // 0 factors or "does not apply"
};

// Function to get color class for volatility level
const getVolatilityColor = (level: string): string => {
  switch (level) {
    case 'Very High':
      return 'bg-red-100 text-red-800';
    case 'High':
      return 'bg-orange-100 text-orange-800';
    case 'Moderate':
      return 'bg-yellow-100 text-yellow-800';
    case 'Low':
      return 'bg-green-100 text-green-800';
    case 'Very Low':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const drivingForces = [
  {
    key: 'longTerm',
    title: 'Long-term Growth',
    description: 'Industry growth rate changes and market size evolution',
    entityType: 'industry_driving.long_term',
  },
  {
    key: 'customer',
    title: 'Customer Changes',
    description: 'Buyer preferences, demographics, and behavior shifts',
    entityType: 'industry_driving.customer',
  },
  {
    key: 'entryExit',
    title: 'Entry & Exit',
    description: 'Major firms entering or leaving the industry',
    entityType: 'industry_driving.entry_exit',
  },
  {
    key: 'innovation',
    title: 'Innovation',
    description: 'Product, service, and process innovations',
    entityType: 'industry_driving.innovation',
  },
  {
    key: 'technicalKnowhow',
    title: 'Technical Know-how',
    description: 'Diffusion of knowledge and technical capabilities',
    entityType: 'industry_driving.technical_knowhow',
  },
  {
    key: 'rulesRegulations',
    title: 'Rules & Regulations',
    description: 'Government policies and regulatory changes',
    entityType: 'industry_driving.rules_regulations',
  },
  {
    key: 'globalisation',
    title: 'Globalisation',
    description: 'International expansion and global market integration',
    entityType: 'industry_driving.globalisation',
  },
  {
    key: 'internet',
    title: 'Internet & Digital',
    description: 'Digital transformation and online business models',
    entityType: 'industry_driving.internet',
  },
  {
    key: 'market',
    title: 'Market Structure',
    description: 'Changes in market dynamics and competitive landscape',
    entityType: 'industry_driving.market',
  },
  {
    key: 'cost',
    title: 'Cost Structure',
    description: 'Changes in cost efficiency and operational factors',
    entityType: 'industry_driving.cost',
  },
  {
    key: 'social',
    title: 'Social Concerns',
    description: 'Societal attitudes, values, and lifestyle changes',
    entityType: 'industry_driving.social',
  },
  {
    key: 'risk',
    title: 'Risk Factors',
    description: 'Uncertainty and risk elements affecting the industry',
    entityType: 'industry_driving.risk',
  },
];

export default function DrivingForcesSection({
  companyId,
  externalData,
  onUpdateAnalysisQuestion,
  onInputFocus,
  onInputBlur,
  onInputChange,
  onProgressChange,
}: DrivingForcesSectionProps) {
  const [activeForce, setActiveForce] = useState('longTerm');
  const [forceScores, setForceScores] = useState<Record<string, number>>({});
  const [backendDrivingForcesData, setBackendDrivingForcesData] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastChangeTime, setLastChangeTime] = useState<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const calculateOverallProgress = useCallback(() => {
    // Simplified progress calculation: 5 answered questions = 100% completion
    const targetAnswers = 5;

    // Count how many driving forces have been analyzed (score >= 0)
    const analyzedForces = Object.values(forceScores).filter((score) => score >= 0);

    // Count analysis questions answered (non-neutral answers)
    const questionsAnswered = Object.values(externalData.analysisQuestions).filter(
      (q) => q && q !== '' && q !== 'neutral' && q !== 'no_change' && q !== 'unchanged'
    ).length;

    // Total answered questions = analyzed forces + analysis questions
    const totalAnsweredQuestions = analyzedForces.length + questionsAnswered;

    return totalAnsweredQuestions >= targetAnswers
      ? 100
      : Math.round((totalAnsweredQuestions / targetAnswers) * 100);
  }, [forceScores, externalData.analysisQuestions]);

  const handleScoreChange = useCallback(
    (entityKey: string, score: number) => {
      setForceScores((prev) => {
        const newScores = {
          ...prev,
          [entityKey]: score,
        };
        return newScores;
      });

      // Only trigger AI analysis animation for user changes, not initial data loading
      if (!isInitialLoad) {
        setIsAnalyzing(true);
        setLastChangeTime(Date.now());
      }
    },
    [isInitialLoad]
  );

  // Update overall progress when force scores or analysis questions change
  useEffect(() => {
    if (onProgressChange) {
      const overallProgress = calculateOverallProgress();
      onProgressChange(overallProgress);
    }
  }, [forceScores, externalData.analysisQuestions, calculateOverallProgress]); // Removed onProgressChange from deps - it's memoized with useCallback

  // Fetch backend driving forces data to ensure consistency with summary page
  const fetchBackendDrivingForcesData = useCallback(async () => {
    try {
      const response = await companiesApi.getSummaryAnalysis(companyId);
      if (response.success && response.data) {
        const summaryData = response.data as any;
        if (summaryData.external?.drivingForcesSummary) {
          setBackendDrivingForcesData(summaryData.external.drivingForcesSummary);
        }
      }
    } catch (error) {
      console.error('Failed to fetch backend driving forces data:', error);
    }
  }, [companyId]);

  // Fetch backend data on initial mount
  useEffect(() => {
    fetchBackendDrivingForcesData();
  }, [fetchBackendDrivingForcesData]);

  // Handle analysis question changes with AI animation
  const handleAnalysisQuestionChange = useCallback(
    (question: 'demandTrend' | 'competitionIntensity' | 'profitabilityImpact', value: string) => {
      onUpdateAnalysisQuestion(question, value);

      // Only trigger AI analysis animation for user changes, not initial data loading
      if (!isInitialLoad) {
        setIsAnalyzing(true);
        setLastChangeTime(Date.now());
      }
    },
    [onUpdateAnalysisQuestion, isInitialLoad]
  );

  // Set initial load to false after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 1000); // Wait 1 second to allow initial data to load

    return () => clearTimeout(timer);
  }, []);

  // Auto-refresh metrics after 3-second AI analysis animation
  useEffect(() => {
    if (lastChangeTime > 0) {
      // Stop analyzing animation after exactly 3 seconds
      const animationTimer = setTimeout(() => {
        setIsAnalyzing(false);
        // Fetch updated data immediately after animation ends
        fetchBackendDrivingForcesData();
      }, 3000); // 3 seconds of AI analysis animation

      return () => clearTimeout(animationTimer);
    }
  }, [lastChangeTime, fetchBackendDrivingForcesData]);

  return (
    <div className="strategy-framework-section">
      {/* Integrated Analysis Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="strategy-framework-summary"
      >
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <TrendingUp className="h-5 w-5 text-orange-600 mr-2" />
              Industry Analysis Summary
            </h3>
          </div>

          {/* Analysis Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 border-r border-gray-200">
                    Industry Volatility
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 border-r border-gray-200">
                    Demand Impact
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 border-r border-gray-200">
                    Competition Impact
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Profitability Impact
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  {/* Industry Volatility Cell */}
                  <td className="px-6 py-4 border-r border-gray-200">
                    <div className="space-y-2">
                      {isAnalyzing ? (
                        <AIAnalysisIndicator isAnalyzing={true} />
                      ) : (
                        (() => {
                          // Use backend result if available, otherwise fall back to frontend calculation
                          if (backendDrivingForcesData) {
                            // Use backend result for consistency with summary page
                            const summary = backendDrivingForcesData;
                            if (summary.analyzedForces === 0 && summary.questionsAnswered === 0) {
                              return (
                                <>
                                  <span className="inline-flex px-3 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                                    Not Analyzed
                                  </span>
                                  <div className="text-xs text-gray-500 mt-1">
                                    0/12 forces + 0/3 questions
                                  </div>
                                </>
                              );
                            }

                            // Use backend's computed volatility (matches complex frontend calculation)
                            const getVolatilityColor = (level: string) => {
                              switch (level) {
                                case 'Very High':
                                  return 'bg-red-100 text-red-800';
                                case 'High':
                                  return 'bg-orange-100 text-orange-800';
                                case 'Moderate':
                                  return 'bg-yellow-100 text-yellow-800';
                                case 'Low':
                                  return 'bg-green-100 text-green-800';
                                case 'Very Low':
                                  return 'bg-blue-100 text-blue-800';
                                default:
                                  return 'bg-gray-100 text-gray-600';
                              }
                            };

                            return (
                              <>
                                <span
                                  className={`inline-flex px-3 py-2 rounded-full text-sm font-medium ${getVolatilityColor(summary.overallVolatilityLevel)}`}
                                >
                                  {summary.overallVolatilityLevel}
                                </span>
                                <div className="text-xs text-gray-500 mt-1">
                                  {summary.analyzedForces}/{summary.totalForces} forces +{' '}
                                  {summary.questionsAnswered}/{summary.totalQuestions} questions
                                  {summary.overallVolatilityScore !== undefined && (
                                    <span className="ml-2 text-gray-400">
                                      ({summary.overallVolatilityScore}% combined)
                                    </span>
                                  )}
                                </div>
                              </>
                            );
                          }

                          // Fall back to frontend calculation
                          const validScores = Object.values(forceScores).filter(
                            (score) => score >= 0
                          );
                          const questionsAnswered = Object.values(
                            externalData.analysisQuestions
                          ).filter(
                            (q) =>
                              q &&
                              q !== '' &&
                              q !== 'neutral' &&
                              q !== 'no_change' &&
                              q !== 'unchanged'
                          ).length;

                          if (validScores.length === 0 && questionsAnswered === 0) {
                            return (
                              <>
                                <span className="inline-flex px-3 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                                  Not Analyzed
                                </span>
                                <div className="text-xs text-gray-500 mt-1">
                                  0/12 forces + 0/3 questions
                                </div>
                              </>
                            );
                          }

                          // Calculate combined score (weighted: 70% forces, 30% questions)
                          const avgForceScore =
                            validScores.length > 0
                              ? validScores.reduce((sum, score) => sum + score, 0) /
                                validScores.length
                              : 0;

                          // Score the analysis questions based on volatility
                          let questionScore = 0; // Start at zero volatility
                          if (questionsAnswered > 0) {
                            let volatilityFactors = 0;

                            // Any change in demand adds volatility
                            if (
                              externalData.analysisQuestions.demandTrend === 'increasing' ||
                              externalData.analysisQuestions.demandTrend === 'decreasing'
                            ) {
                              volatilityFactors++;
                            }

                            // Any change in competition adds volatility
                            if (
                              externalData.analysisQuestions.competitionIntensity ===
                                'more_intense' ||
                              externalData.analysisQuestions.competitionIntensity === 'less_intense'
                            ) {
                              volatilityFactors++;
                            }

                            // Any change in profitability adds volatility
                            if (
                              externalData.analysisQuestions.profitabilityImpact === 'higher' ||
                              externalData.analysisQuestions.profitabilityImpact === 'lower'
                            ) {
                              volatilityFactors++;
                            }

                            // Convert to 0-100 scale (each factor adds ~33.33 points)
                            questionScore = volatilityFactors * 33.33;
                          }

                          const combinedScore = Math.round(
                            avgForceScore * 0.7 + questionScore * 0.3
                          );
                          const volatilityResult = calculateDrivingForcesVolatility(
                            combinedScore,
                            100
                          );

                          return (
                            <>
                              <span
                                className={`inline-flex px-3 py-2 rounded-full text-sm font-medium ${volatilityResult.color}`}
                              >
                                {volatilityResult.level}
                              </span>
                              <div className="text-xs text-gray-500 mt-1">
                                {validScores.length}/12 forces + {questionsAnswered}/3 questions
                              </div>
                            </>
                          );
                        })()
                      )}
                    </div>
                  </td>

                  {/* Demand Impact Cell */}
                  <td className="px-6 py-4 border-r border-gray-200">
                    <div className="space-y-2">
                      {[
                        {
                          value: 'increasing',
                          label: 'Increasing',
                          color: 'text-green-700 bg-green-50 border-green-200',
                        },
                        {
                          value: 'decreasing',
                          label: 'Decreasing',
                          color: 'text-red-700 bg-red-50 border-red-200',
                        },
                        {
                          value: 'neutral',
                          label: 'Neutral',
                          color: 'text-gray-700 bg-gray-50 border-gray-200',
                        },
                      ].map((option) => {
                        const isSelected =
                          externalData.analysisQuestions.demandTrend === option.value;

                        // Define colors based on semantic meaning
                        const getCheckboxColors = (value: string) => {
                          switch (value) {
                            case 'increasing':
                              return {
                                selected: 'border-green-500 bg-green-500',
                                hover: 'group-hover:border-green-300',
                              };
                            case 'decreasing':
                              return {
                                selected: 'border-red-500 bg-red-500',
                                hover: 'group-hover:border-red-300',
                              };
                            default:
                              return {
                                selected: 'border-gray-500 bg-gray-500',
                                hover: 'group-hover:border-gray-400',
                              };
                          }
                        };

                        const colors = getCheckboxColors(option.value);

                        return (
                          <label
                            key={option.value}
                            className="flex items-center cursor-pointer group"
                          >
                            <input
                              type="radio"
                              name="demandTrend"
                              value={option.value}
                              checked={isSelected}
                              onChange={(e) => {
                                handleAnalysisQuestionChange('demandTrend', e.target.value);
                                onInputChange();
                              }}
                              onFocus={onInputFocus}
                              onBlur={onInputBlur}
                              className="sr-only"
                            />
                            <div
                              className={`relative mr-3 w-5 h-5 rounded border-2 transition-all duration-200 ${
                                isSelected
                                  ? colors.selected
                                  : `border-gray-300 hover:border-gray-400 ${colors.hover}`
                              }`}
                            >
                              {isSelected && <Check className="h-3 w-3 text-white m-0.5" />}
                            </div>
                            <span
                              className={`text-sm px-2 py-1 rounded border transition-colors ${
                                isSelected ? option.color : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </td>

                  {/* Competition Impact Cell */}
                  <td className="px-6 py-4 border-r border-gray-200">
                    <div className="space-y-2">
                      {[
                        {
                          value: 'more_intense',
                          label: 'More Intense',
                          color: 'text-red-700 bg-red-50 border-red-200',
                        },
                        {
                          value: 'less_intense',
                          label: 'Less Intense',
                          color: 'text-green-700 bg-green-50 border-green-200',
                        },
                        {
                          value: 'no_change',
                          label: 'No Change',
                          color: 'text-gray-700 bg-gray-50 border-gray-200',
                        },
                      ].map((option) => {
                        const isSelected =
                          externalData.analysisQuestions.competitionIntensity === option.value;

                        // Define colors based on semantic meaning
                        const getCheckboxColors = (value: string) => {
                          switch (value) {
                            case 'more_intense':
                              return {
                                selected: 'border-red-500 bg-red-500',
                                hover: 'group-hover:border-red-300',
                              };
                            case 'less_intense':
                              return {
                                selected: 'border-green-500 bg-green-500',
                                hover: 'group-hover:border-green-300',
                              };
                            default:
                              return {
                                selected: 'border-gray-500 bg-gray-500',
                                hover: 'group-hover:border-gray-400',
                              };
                          }
                        };

                        const colors = getCheckboxColors(option.value);

                        return (
                          <label
                            key={option.value}
                            className="flex items-center cursor-pointer group"
                          >
                            <input
                              type="radio"
                              name="competitionIntensity"
                              value={option.value}
                              checked={isSelected}
                              onChange={(e) => {
                                handleAnalysisQuestionChange(
                                  'competitionIntensity',
                                  e.target.value
                                );
                                onInputChange();
                              }}
                              onFocus={onInputFocus}
                              onBlur={onInputBlur}
                              className="sr-only"
                            />
                            <div
                              className={`relative mr-3 w-5 h-5 rounded border-2 transition-all duration-200 ${
                                isSelected
                                  ? colors.selected
                                  : `border-gray-300 hover:border-gray-400 ${colors.hover}`
                              }`}
                            >
                              {isSelected && <Check className="h-3 w-3 text-white m-0.5" />}
                            </div>
                            <span
                              className={`text-sm px-2 py-1 rounded border transition-colors ${
                                isSelected ? option.color : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </td>

                  {/* Profitability Impact Cell */}
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      {[
                        {
                          value: 'higher',
                          label: 'Higher',
                          color: 'text-green-700 bg-green-50 border-green-200',
                        },
                        {
                          value: 'lower',
                          label: 'Lower',
                          color: 'text-red-700 bg-red-50 border-red-200',
                        },
                        {
                          value: 'unchanged',
                          label: 'Unchanged',
                          color: 'text-gray-700 bg-gray-50 border-gray-200',
                        },
                      ].map((option) => {
                        const isSelected =
                          externalData.analysisQuestions.profitabilityImpact === option.value;

                        // Define colors based on semantic meaning
                        const getCheckboxColors = (value: string) => {
                          switch (value) {
                            case 'higher':
                              return {
                                selected: 'border-green-500 bg-green-500',
                                hover: 'group-hover:border-green-300',
                              };
                            case 'lower':
                              return {
                                selected: 'border-red-500 bg-red-500',
                                hover: 'group-hover:border-red-300',
                              };
                            default:
                              return {
                                selected: 'border-gray-500 bg-gray-500',
                                hover: 'group-hover:border-gray-400',
                              };
                          }
                        };

                        const colors = getCheckboxColors(option.value);

                        return (
                          <label
                            key={option.value}
                            className="flex items-center cursor-pointer group"
                          >
                            <input
                              type="radio"
                              name="profitabilityImpact"
                              value={option.value}
                              checked={isSelected}
                              onChange={(e) => {
                                handleAnalysisQuestionChange('profitabilityImpact', e.target.value);
                                onInputChange();
                              }}
                              onFocus={onInputFocus}
                              onBlur={onInputBlur}
                              className="sr-only"
                            />
                            <div
                              className={`relative mr-3 w-5 h-5 rounded border-2 transition-all duration-200 ${
                                isSelected
                                  ? colors.selected
                                  : `border-gray-300 hover:border-gray-400 ${colors.hover}`
                              }`}
                            >
                              {isSelected && <Check className="h-3 w-3 text-white m-0.5" />}
                            </div>
                            <span
                              className={`text-sm px-2 py-1 rounded border transition-colors ${
                                isSelected ? option.color : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* Two Column Layout - Compact Forces Left, Wider Checklist Right */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="strategy-framework-grid"
      >
        {/* Left Side - Force Navigation */}
        <div className="strategy-framework-sidebar flex flex-col space-y-4">
          {/* Force Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center mb-4">
              <TrendingUp className="h-5 w-5 text-orange-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Driving Forces</h2>
              <div className="ml-auto text-right">
                <div className="text-sm text-gray-500">Select a force</div>
              </div>
            </div>

            <div className="space-y-2">
              {drivingForces.map((force) => {
                const forceScore = forceScores[force.key] ?? -1;
                const volatility = getVolatilityLevel(forceScore);
                const volatilityColor = getVolatilityColor(volatility);
                const hasProgress = forceScore >= 0;

                return (
                  <button
                    key={force.key}
                    onClick={() => setActiveForce(force.key)}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      activeForce === force.key
                        ? 'bg-orange-100 border-2 border-orange-300'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{force.title}</div>
                        <div className="text-xs text-gray-600 mt-1">{force.description}</div>
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        <div
                          className={`w-2 h-2 rounded-full ${hasProgress ? 'bg-orange-500' : 'bg-gray-300'}`}
                        />
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${volatilityColor}`}
                        >
                          {volatility}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Force Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                {drivingForces.find((force) => force.key === activeForce)?.title} Analysis
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {drivingForces.find((force) => force.key === activeForce)?.description}
            </p>
            <div className="bg-orange-50 rounded-lg p-3">
              <p className="text-sm text-orange-800">
                Analyze factors that are driving change in this area. Consider both current trends
                and emerging developments that could impact your industry.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Wider Checklist */}
        <div className="xl:col-span-3">
          {drivingForces.map((force) => (
            <div key={force.key} className={activeForce === force.key ? 'block h-full' : 'hidden'}>
              <DrivingForcesChecklist
                entityType={force.entityType}
                entityKey={force.key}
                companyId={companyId}
                title={`${force.title} Analysis`}
                onScoreChange={(score: number) => handleScoreChange(force.key, score)}
                className="h-full"
              />
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
